import React, { useState, useEffect, useCallback } from 'react';
import { Filter, Download, Eye, CheckCircle, XCircle, Clock, Send, UserPlus, FileText } from 'lucide-react';
import { Card } from '../../../shared/components/ui/card';
import { Button } from '../../../shared/components/ui/button';
import { Input } from '../../../shared/components/ui/input';
import { Label } from '../../../shared/components/ui/label';
import { Badge } from '../../../shared/components/ui/badge';
import { Table } from '../../../shared/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../shared/components/ui/select';
import ServiceContainer from '../../../core/services/ServiceContainer';

const serviceContainer = ServiceContainer.getInstance();
const apiClient = serviceContainer.getApiClient();

type ActiveTab = 'messages' | 'users' | 'templates';

interface MessageLog {
  id: string;
  userId: string;
  messageId: string;
  clientId: string;
  damId: string;
  messageBody: string;
  messageType: 'SMS' | 'WHATSAPP';
  totalRecipients: number;
  successCount: number;
  failureCount: number;
  status: 'PENDING' | 'SENT' | 'FAILED';
  groupIds: string[];
  createdAt: string;
  user: {
    id: string;
    name: string;
    email: string;
  } | null;
  client: {
    id: string;
    name: string;
  } | null;
  dam: {
    id: string;
    name: string;
  } | null;
  message: {
    id: string;
    body: string;
    type: string;
    status: string;
    createdAt: string;
    recipients?: Array<{
      id: string;
      to: string;
      status: 'PENDING' | 'SENT' | 'FAILED';
      lastError?: string | null;
      contact?: {
        id: string;
        name: string;
      } | null;
    }>;
  };
}

interface ActivityLog {
  id: string;
  userId: string;
  action: string;
  entityType: string;
  entityId: string;
  entityName?: string;
  metadata?: any;
  createdAt: string;
  user: {
    id: string;
    name: string;
    email: string;
  } | null;
}

export function LogsPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('messages');

  // Message Logs state
  const [logs, setLogs] = useState<MessageLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<MessageLog | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const [filterUser, setFilterUser] = useState('');
  const [filterDam, setFilterDam] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dams, setDams] = useState<Array<{ id: string; name: string }>>([]);

  // Activity Logs state (Users + Templates)
  const [userLogs, setUserLogs] = useState<ActivityLog[]>([]);
  const [templateLogs, setTemplateLogs] = useState<ActivityLog[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const [filterActivityUserSearch, setFilterActivityUserSearch] = useState('');
  const [filterActivityStartDate, setFilterActivityStartDate] = useState('');
  const [filterActivityEndDate, setFilterActivityEndDate] = useState('');

  // Fetch message logs
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterDam) params.append('damId', filterDam);
      if (filterStartDate) params.append('startDate', filterStartDate);
      if (filterEndDate) params.append('endDate', filterEndDate);
      if (filterType) params.append('messageType', filterType);
      if (filterStatus) params.append('status', filterStatus);

      const logsData = await apiClient.get<MessageLog[]>(`/message-logs?${params.toString()}`, {
        headers: {
          'x-cache-ttl-ms': 20000,
        },
      });

      const filteredLogs = filterUser
        ? logsData.filter((log: MessageLog) =>
            (log.user?.name || log.user?.email || '').toLowerCase().includes(filterUser.toLowerCase())
          )
        : logsData;
      setLogs(filteredLogs);
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filterUser, filterDam, filterStartDate, filterEndDate, filterType, filterStatus]);

  const fetchDams = useCallback(async () => {
    try {
      const data = await apiClient.get<Array<{ id: string; name: string }>>('/dams', {
        headers: {
          'x-cache-ttl-ms': 60000,
        },
      });
      setDams(data);
    } catch (error) {
      console.error('Erro ao buscar barragens:', error);
    }
  }, []);

  // Fetch activity logs
  const fetchActivityLogs = useCallback(async (entityType: 'USER' | 'MESSAGE_TEMPLATE') => {
    try {
      setActivityLoading(true);
      const params = new URLSearchParams();
      params.append('entityType', entityType);
      if (filterActivityStartDate) params.append('startDate', filterActivityStartDate);
      if (filterActivityEndDate) params.append('endDate', filterActivityEndDate);

      const data = await apiClient.get<ActivityLog[]>(`/activity-logs?${params.toString()}`, {
        headers: {
          'x-cache-ttl-ms': 20000,
        },
      });

      const createDeleteOnly = data.filter((l) => l.action === 'CREATE' || l.action === 'DELETE');
      const filtered = filterActivityUserSearch
        ? createDeleteOnly.filter((l) =>
            (l.entityName || '').toLowerCase().includes(filterActivityUserSearch.toLowerCase()) ||
            (l.user?.name || l.user?.email || '').toLowerCase().includes(filterActivityUserSearch.toLowerCase())
          )
        : createDeleteOnly;
      if (entityType === 'USER') setUserLogs(filtered);
      else setTemplateLogs(filtered);
    } catch (error) {
      console.error('Erro ao buscar activity logs:', error);
    } finally {
      setActivityLoading(false);
    }
  }, [filterActivityUserSearch, filterActivityStartDate, filterActivityEndDate]);

  useEffect(() => {
    fetchLogs();
    fetchDams();
  }, [fetchLogs, fetchDams]);

  useEffect(() => {
    if (activeTab === 'users') fetchActivityLogs('USER');
    if (activeTab === 'templates') fetchActivityLogs('MESSAGE_TEMPLATE');
  }, [activeTab, fetchActivityLogs]);

  useEffect(() => {
    const refreshLogs = () => {
      if (activeTab === 'messages') {
        void fetchLogs();
      }
    };

    window.addEventListener('snm:messages-sent', refreshLogs);
    return () => {
      window.removeEventListener('snm:messages-sent', refreshLogs);
    };
  }, [activeTab, fetchLogs]);

  // Message logs helpers
  const clearFilters = () => {
    setFilterUser('');
    setFilterDam('');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterType('');
    setFilterStatus('');
  };

  const clearActivityFilters = () => {
    setFilterActivityUserSearch('');
    setFilterActivityStartDate('');
    setFilterActivityEndDate('');
  };

  const exportToCSV = () => {
    const escapeCSV = (value: any): string => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (stringValue.includes(';') || stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    let headers: string[] = [];
    let rows: string[][] = [];
    let filenamePrefix = 'logs';

    if (activeTab === 'messages') {
      headers = ['Data', 'Hora', 'Usuário', 'E-mail', 'Cliente', 'Barragem', 'Mensagem', 'Total Destinatários', 'Sucessos', 'Falhas', 'Tipo', 'Status'];
      const statusMap: Record<string, string> = { 'SENT': 'Concluído', 'PENDING': 'Em Andamento', 'FAILED': 'Erro' };
      const typeMap: Record<string, string> = { 'SMS': 'SMS', 'WHATSAPP': 'WhatsApp' };

      rows = logs.map((log) => {
        const date = new Date(log.createdAt);
        return [
          escapeCSV(date.toLocaleDateString('pt-BR')),
          escapeCSV(date.toLocaleTimeString('pt-BR')),
          escapeCSV(log.user?.name || ''),
          escapeCSV(log.user?.email || ''),
          escapeCSV(log.client?.name || ''),
          escapeCSV(log.dam?.name || ''),
          escapeCSV(log.messageBody),
          escapeCSV(log.totalRecipients),
          escapeCSV(log.successCount),
          escapeCSV(log.failureCount),
          escapeCSV(typeMap[log.messageType] || log.messageType),
          escapeCSV(statusMap[log.status] || log.status),
        ];
      });
      filenamePrefix = 'logs-envios';
    }

    if (activeTab === 'users') {
      headers = ['Data', 'Hora', 'Ação', 'Responsável', 'Usuário', 'E-mail', 'Perfis'];
      rows = userLogs.map((log) => {
        const date = new Date(log.createdAt);
        const roles = Array.isArray(log.metadata?.roles) ? log.metadata.roles.join(', ') : '';
        return [
          escapeCSV(date.toLocaleDateString('pt-BR')),
          escapeCSV(date.toLocaleTimeString('pt-BR')),
          escapeCSV(log.action === 'DELETE' ? 'Excluído' : 'Criado'),
          escapeCSV(log.user?.name || log.user?.email || ''),
          escapeCSV(log.entityName || log.metadata?.targetName || ''),
          escapeCSV(log.metadata?.targetEmail || ''),
          escapeCSV(roles),
        ];
      });
      filenamePrefix = 'logs-usuarios';
    }

    if (activeTab === 'templates') {
      headers = ['Data', 'Hora', 'Ação', 'Responsável', 'Template', 'Conteúdo'];
      rows = templateLogs.map((log) => {
        const date = new Date(log.createdAt);
        return [
          escapeCSV(date.toLocaleDateString('pt-BR')),
          escapeCSV(date.toLocaleTimeString('pt-BR')),
          escapeCSV(log.action === 'DELETE' ? 'Excluído' : 'Criado'),
          escapeCSV(log.user?.name || log.user?.email || ''),
          escapeCSV(log.entityName || ''),
          escapeCSV(log.metadata?.body || ''),
        ];
      });
      filenamePrefix = 'logs-templates';
    }

    const csvContent = [headers, ...rows].map((row) => row.join(';')).join('\r\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filenamePrefix}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SENT': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'PENDING': return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'FAILED': return <XCircle className="w-5 h-5 text-red-600" />;
      default: return null;
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = { 'SENT': 'Concluído', 'PENDING': 'Em Andamento', 'FAILED': 'Erro' };
    return labels[status] || status;
  };

  const getStatusVariant = (status: string): 'default' | 'destructive' | 'outline' | 'secondary' => {
    const variants: Record<string, 'default' | 'destructive' | 'outline' | 'secondary'> = {
      'SENT': 'default', 'PENDING': 'secondary', 'FAILED': 'destructive',
    };
    return variants[status] || 'default';
  };

  const getStatusClass = (status: string): string => {
    const classes: Record<string, string> = {
      'SENT': 'bg-green-100 text-green-700 border-green-200',
      'PENDING': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'FAILED': '',
    };
    return classes[status] || '';
  };

  const getTypeLabel = (type: string) => type === 'SMS' ? 'SMS' : 'WhatsApp';

  const handleViewDetails = async (log: MessageLog) => {
    setSelectedLog(log);
    setIsDetailsOpen(true);

    try {
      const detailedLog = await apiClient.get<MessageLog>(`/message-logs/${log.id}`, {
        headers: {
          'x-cache-ttl-ms': 20000,
        },
      });
      setSelectedLog(detailedLog);
    } catch (error) {
      console.error('Erro ao buscar detalhes do log:', error);
    }
  };

  // Tab button style helper
  const tabClass = (tab: ActiveTab) =>
    `flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-colors ${
      activeTab === tab
        ? 'bg-blue-600 text-white shadow-sm'
        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
    }`;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Logs</h1>
          <p className="text-slate-600 mt-2">Histórico de atividades do sistema</p>
        </div>
        <Button onClick={exportToCSV} className="flex items-center gap-2">
          <Download className="w-5 h-5" />
          Exportar CSV
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-3 mb-4">
        <button className={tabClass('messages')} onClick={() => setActiveTab('messages')}>
          <Send className="w-4 h-4" />
          Logs de Envio
        </button>
        <button className={tabClass('users')} onClick={() => setActiveTab('users')}>
          <UserPlus className="w-4 h-4" />
          Logs de Criação de Usuário
        </button>
        <button className={tabClass('templates')} onClick={() => setActiveTab('templates')}>
          <FileText className="w-4 h-4" />
          Log de Templates
        </button>
      </div>

      {/* Message Logs Tab */}
      {activeTab === 'messages' && (
        <>
          <Card className="mb-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-bold text-slate-900">Filtros de Pesquisa</h2>
              </div>
              {(filterUser || filterDam || filterStartDate || filterEndDate || filterType || filterStatus) && (
                <Button onClick={clearFilters} variant="outline" size="sm" className="text-slate-600">
                  Limpar filtros
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-sm font-medium text-slate-700">Pesquisar por Usuário</Label>
                <Input value={filterUser} onChange={(e) => setFilterUser(e.target.value)} placeholder="Digite o nome do usuário..." className="h-10" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm font-medium text-slate-700">Barragem</Label>
                <Select value={filterDam} onValueChange={setFilterDam}>
                  <SelectTrigger className="h-10"><SelectValue placeholder="Todas as barragens" /></SelectTrigger>
                  <SelectContent>
                    {dams.map((dam) => (
                      <SelectItem key={dam.id} value={dam.id}>{dam.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-sm font-medium text-slate-700">Tipo de Mensagem</Label>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="h-10"><SelectValue placeholder="Todos os tipos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SMS">SMS</SelectItem>
                    <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-sm font-medium text-slate-700">Status do Envio</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="h-10"><SelectValue placeholder="Todos os status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SENT">Concluído</SelectItem>
                    <SelectItem value="PENDING">Em Andamento</SelectItem>
                    <SelectItem value="FAILED">Erro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-sm font-medium text-slate-700">Data Início</Label>
                <Input type="date" value={filterStartDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterStartDate(e.target.value)} className="h-10" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm font-medium text-slate-700">Data Fim</Label>
                <Input type="date" value={filterEndDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterEndDate(e.target.value)} className="h-10" />
              </div>
            </div>
          </Card>

          <Card>
            {loading ? (
              <div className="text-center py-12"><p className="text-slate-600">Carregando logs...</p></div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Nenhum log encontrado</h3>
                <p className="text-slate-600">Ajuste os filtros ou envie mensagens para ver os logs</p>
              </div>
            ) : (
              <div className="max-h-[62vh] overflow-y-auto">
                <Table>
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-4 font-semibold text-slate-700">Data/Hora</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Usuário</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Mensagem</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Barragem</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Destinatários</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Tipo</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Status</th>
                      <th className="text-right p-4 font-semibold text-slate-700">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => {
                      const date = new Date(log.createdAt);
                      return (
                        <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-4">
                            <div>
                              <p className="font-medium text-slate-900">{date.toLocaleDateString('pt-BR')}</p>
                              <p className="text-sm text-slate-600">{date.toLocaleTimeString('pt-BR')}</p>
                            </div>
                          </td>
                          <td className="p-4 text-slate-900">{log.user?.name || log.user?.email || '-'}</td>
                          <td className="p-4 text-slate-600 text-sm max-w-xs truncate">{log.messageBody}</td>
                          <td className="p-4 text-slate-900">{log.dam?.name || '-'}</td>
                          <td className="p-4">
                            <div className="text-sm">
                              <p className="text-slate-900 font-medium">{log.totalRecipients} total</p>
                              <p className="text-green-600">{log.successCount} enviados</p>
                              {log.failureCount > 0 && <p className="text-red-600">{log.failureCount} falhas</p>}
                            </div>
                          </td>
                          <td className="p-4"><Badge variant="default">{getTypeLabel(log.messageType)}</Badge></td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(log.status)}
                              <Badge variant={getStatusVariant(log.status)} className={getStatusClass(log.status)}>
                                {getStatusLabel(log.status)}
                              </Badge>
                            </div>
                          </td>
                          <td className="p-4 text-right">
                            <Button variant="ghost" size="sm" onClick={() => handleViewDetails(log)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>

          <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Detalhes do Envio</DialogTitle></DialogHeader>
              {selectedLog && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Data e Hora</p>
                      <p className="font-medium text-slate-900">
                        {new Date(selectedLog.createdAt).toLocaleDateString('pt-BR')} às{' '}
                        {new Date(selectedLog.createdAt).toLocaleTimeString('pt-BR')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Usuário Responsável</p>
                      <p className="font-medium text-slate-900">{selectedLog.user?.name || selectedLog.user?.email || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Tipo de Envio</p>
                      <p className="font-medium text-slate-900">{getTypeLabel(selectedLog.messageType)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Status</p>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(selectedLog.status)}
                        <span className="font-medium text-slate-900">{getStatusLabel(selectedLog.status)}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Mensagem</p>
                    <p className="font-medium text-slate-900">{selectedLog.messageBody}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Barragem</p>
                    <p className="font-medium text-slate-900">{selectedLog.dam?.name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Cliente</p>
                    <p className="font-medium text-slate-900">{selectedLog.client?.name || '-'}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-200">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900">{selectedLog.totalRecipients}</p>
                      <p className="text-sm text-slate-600">Total</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{selectedLog.successCount}</p>
                      <p className="text-sm text-slate-600">Sucessos</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">{selectedLog.failureCount}</p>
                      <p className="text-sm text-slate-600">Falhas</p>
                    </div>
                  </div>

                  {selectedLog.failureCount > 0 && (
                    <div className="pt-4 border-t border-slate-200">
                      <p className="text-sm text-slate-600 mb-2">Números com erro</p>
                      <div className="max-h-48 overflow-y-auto rounded-md border border-red-200 bg-red-50/30">
                        {(() => {
                          const failedRecipients = (selectedLog.message?.recipients || []).filter(
                            (recipient) => recipient.status === 'FAILED',
                          );

                          if (failedRecipients.length === 0) {
                            return (
                              <p className="p-3 text-sm text-slate-600">
                                Não foi possível detalhar os números com erro para este envio.
                              </p>
                            );
                          }

                          return failedRecipients.map((recipient) => (
                            <div key={recipient.id} className="p-3 border-b border-red-100 last:border-b-0">
                              <p className="text-sm font-medium text-slate-900">
                                {recipient.contact?.name ? `${recipient.contact.name} - ` : ''}
                                {recipient.to}
                              </p>
                              <p className="text-sm text-red-700">
                                {recipient.lastError || 'Erro não informado pelo provedor'}
                              </p>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  )}

                  <Button onClick={() => setIsDetailsOpen(false)} variant="secondary" className="w-full mt-4">
                    Fechar
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* User Creation Logs Tab */}
      {activeTab === 'users' && (
        <>
          <Card className="mb-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-bold text-slate-900">Filtros de Pesquisa</h2>
              </div>
              {(filterActivityUserSearch || filterActivityStartDate || filterActivityEndDate) && (
                <Button onClick={clearActivityFilters} variant="outline" size="sm" className="text-slate-600">
                  Limpar filtros
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-sm font-medium text-slate-700">Pesquisar</Label>
                <Input value={filterActivityUserSearch} onChange={(e) => setFilterActivityUserSearch(e.target.value)} placeholder="Nome do usuário ou criador..." className="h-10" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm font-medium text-slate-700">Data Início</Label>
                <Input type="date" value={filterActivityStartDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterActivityStartDate(e.target.value)} className="h-10" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm font-medium text-slate-700">Data Fim</Label>
                <Input type="date" value={filterActivityEndDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterActivityEndDate(e.target.value)} className="h-10" />
              </div>
            </div>
          </Card>

          <Card>
            {activityLoading ? (
              <div className="text-center py-12"><p className="text-slate-600">Carregando logs...</p></div>
            ) : userLogs.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Nenhum log encontrado</h3>
                <p className="text-slate-600">Nenhuma criação/exclusão de usuário registrada</p>
              </div>
            ) : (
              <div className="max-h-[62vh] overflow-y-auto">
                <Table>
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-4 font-semibold text-slate-700">Data/Hora</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Ação</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Criado por</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Novo Usuário</th>
                      <th className="text-left p-4 font-semibold text-slate-700">E-mail</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Perfis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userLogs.map((log) => {
                      const date = new Date(log.createdAt);
                      const roles: string[] = log.metadata?.roles || [];
                      return (
                        <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-4">
                            <p className="font-medium text-slate-900">{date.toLocaleDateString('pt-BR')}</p>
                            <p className="text-sm text-slate-600">{date.toLocaleTimeString('pt-BR')}</p>
                          </td>
                          <td className="p-4">
                            <Badge variant={log.action === 'DELETE' ? 'destructive' : 'default'}>
                              {log.action === 'DELETE' ? 'Excluído' : 'Criado'}
                            </Badge>
                          </td>
                          <td className="p-4 text-slate-900">{log.user?.name || log.user?.email || '-'}</td>
                          <td className="p-4 text-slate-900">{log.entityName || log.metadata?.targetName || '-'}</td>
                          <td className="p-4 text-slate-600 text-sm">{log.metadata?.targetEmail || '-'}</td>
                          <td className="p-4">
                            <div className="flex flex-wrap gap-1">
                              {roles.length > 0
                                ? roles.map((r: string) => (
                                    <Badge key={r} variant="secondary" className="text-xs capitalize">{r}</Badge>
                                  ))
                                : <span className="text-slate-400 text-sm">-</span>
                              }
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* Template Logs Tab */}
      {activeTab === 'templates' && (
        <>
          <Card className="mb-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-bold text-slate-900">Filtros de Pesquisa</h2>
              </div>
              {(filterActivityUserSearch || filterActivityStartDate || filterActivityEndDate) && (
                <Button onClick={clearActivityFilters} variant="outline" size="sm" className="text-slate-600">
                  Limpar filtros
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-sm font-medium text-slate-700">Pesquisar</Label>
                <Input value={filterActivityUserSearch} onChange={(e) => setFilterActivityUserSearch(e.target.value)} placeholder="Nome do template ou criador..." className="h-10" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm font-medium text-slate-700">Data Início</Label>
                <Input type="date" value={filterActivityStartDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterActivityStartDate(e.target.value)} className="h-10" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm font-medium text-slate-700">Data Fim</Label>
                <Input type="date" value={filterActivityEndDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterActivityEndDate(e.target.value)} className="h-10" />
              </div>
            </div>
          </Card>

          <Card>
            {activityLoading ? (
              <div className="text-center py-12"><p className="text-slate-600">Carregando logs...</p></div>
            ) : templateLogs.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Nenhum log encontrado</h3>
                <p className="text-slate-600">Nenhuma criação/exclusão de template registrada</p>
              </div>
            ) : (
              <div className="max-h-[62vh] overflow-y-auto">
                <Table>
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-4 font-semibold text-slate-700">Data/Hora</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Ação</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Criado por</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Template</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Conteúdo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templateLogs.map((log) => {
                      const date = new Date(log.createdAt);
                      return (
                        <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-4">
                            <p className="font-medium text-slate-900">{date.toLocaleDateString('pt-BR')}</p>
                            <p className="text-sm text-slate-600">{date.toLocaleTimeString('pt-BR')}</p>
                          </td>
                          <td className="p-4">
                            <Badge variant={log.action === 'DELETE' ? 'destructive' : 'default'}>
                              {log.action === 'DELETE' ? 'Excluído' : 'Criado'}
                            </Badge>
                          </td>
                          <td className="p-4 text-slate-900">{log.user?.name || log.user?.email || '-'}</td>
                          <td className="p-4 text-slate-900 font-medium">{log.entityName || '-'}</td>
                          <td className="p-4 text-slate-600 text-sm max-w-xs truncate">{log.metadata?.body || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
